
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PieChart from "examples/Charts/PieChart";
import ChartHeader from "examples/Charts/PieChart/ChartHeader";
import ChartNotEntitled from "examples/Charts/General/ChartNotEntitled"; // ✅ Added Import
import { generateColors } from "../../../utils/chartColors";
import Icon from "@mui/material/Icon";
import MetricCardsContainer from "../components/MetricCardsContainer";
import { Typography } from "@mui/material";
import EntitiesPopup from "layouts/components/EntitiesPopup";
import ChartAssistant from "components/ChartAssistant";

import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";

// -----------------------------------------------------------
// 🚀 HELPER: Parse Audience Gender
// -----------------------------------------------------------
const parseGenderDemographics = (profile) => {
    let demoObj = null;

    // 1. Check Root
    if (profile.profileDemographics) {
        demoObj = profile.profileDemographics;
    }
    // 1b. Check YouTube Root
    else if (profile.channelDemographics?.gender && Object.keys(profile.channelDemographics.gender).length > 0) {
        demoObj = {
            audience_gender: profile.channelDemographics.gender || {}
        };
    }
    // 2. Check History
    else if (profile.data && profile.data.length > 0) {
        const sortedData = [...profile.data].sort((a, b) => {
            const dateA = new Date(a.date || a.datetime || 0);
            const dateB = new Date(b.date || b.datetime || 0);
            return dateB - dateA;
        });

        const latestItem = sortedData.find(item =>
            (item.profileDemographics?.audience_gender_age || item.profileDemographics?.audienceGenderAge || item.profileDemographics?.audience_gender || item.profileDemographics?.audienceGender) ||
            (item.channelDemographics?.gender && Object.keys(item.channelDemographics.gender).length > 0) // ✅ YouTube History Robust Check
        );

        if (latestItem) {
            if (latestItem.channelDemographics?.gender) {
                demoObj = { audience_gender: latestItem.channelDemographics.gender };
            } else {
                demoObj = latestItem.profileDemographics;
            }
        }
    }

    if (!demoObj) return null;

    // Normalize Keys
    const audience_gender = demoObj.audience_gender || demoObj.audienceGender || {};

    // ✅ NEW: Fallback to consolidated audience_gender_age
    if (Object.keys(audience_gender).length === 0) {
        const genderAge = demoObj.audience_gender_age || demoObj.audienceGenderAge || {};
        if (Object.keys(genderAge).length > 0) {
            // Aggregate keys like "F_18-24"
            Object.entries(genderAge).forEach(([key, value]) => {
                const parts = key.split('_');
                // Assumption: Key is Format "Gender_Age" per backend logic
                if (parts.length === 2) {
                    const gender = parts[0];
                    audience_gender[gender] = (audience_gender[gender] || 0) + value;
                } else {
                    // Fallback: Check if key itself is F/M/U (unlikely if strictly following schema but safe)
                    if (["F", "M", "U"].includes(key)) {
                        audience_gender[key] = (audience_gender[key] || 0) + value;
                    }
                }
            });
        }
    }

    if (Object.keys(audience_gender).length === 0) return null;

    // Sort labels for consistency
    const rawLabels = Object.keys(audience_gender).sort();

    // ✅ Apply Label Mapping
    const labelMap = {
        "M": "Male",
        "F": "Female",
        "U": "Gender Undetermined",
        "male": "Male",
        "female": "Female",
        "user_specified": "Gender Undetermined"
    };

    const labels = rawLabels.map(l => labelMap[l] || l);
    const data = rawLabels.map(l => audience_gender[l] || 0);

    // Find Top Gender
    let maxVal = -1;
    let maxLabel = "N/A";
    labels.forEach((label, idx) => {
        if (data[idx] > maxVal) {
            maxVal = data[idx];
            maxLabel = label;
        }
    });

    // Generate Colors (Safe generation based on labels)
    // Optional: Could map specific colors to "male"/"female" here if desired, 
    // but generating is safer for unknown keys.
    const backgroundColors = generateColors(labels.length, labels);

    return {
        labels,
        datasets: {
            label: "Audience Gender",
            data: data,
            backgroundColors: backgroundColors
        },
        topGender: maxLabel
    };
};

// -----------------------------------------------------------
// 🚀 COMPONENT
// -----------------------------------------------------------
function AudienceGenderChart({
    title,
    datasets,
    previousdatasets = [],
    isLoading = false,
    is_derived = false,
    entitlements,
    isEntitled = true, // ✅ Added
    featureName = "Demographics Report", // ✅ Added
    planRequired = "pro" // ✅ Added
}) {

    // 1. Calculate Chart Data
    const chartDataByProfile = useMemo(() => {
        if (!datasets || datasets.length === 0) return [];

        return datasets.map(profile => {
            const parsed = parseGenderDemographics(profile);
            if (!parsed) return null;

            return {
                profileName: profile.label || profile.name || profile.username || "Profile",
                platform: profile.platform,
                profileId: profile.profileId,
                chart: {
                    labels: parsed.labels,
                    datasets: parsed.datasets
                },
                topGender: parsed.topGender
            };
        }).filter(Boolean);
    }, [datasets]);

    // 2. Calculate Metrics (Top Gender & Previous)
    const metrics = useMemo(() => {
        if (!datasets) return [];

        return datasets.map(profile => {
            const parsed = parseGenderDemographics(profile);

            // Find Previous
            const prevProfile = Array.isArray(previousdatasets)
                ? previousdatasets.find(p => p.profileId === (profile.profileId || profile.id))
                : null;

            let prevTopGender = "N/A";
            if (prevProfile) {
                const prevParsed = parseGenderDemographics(prevProfile);
                if (prevParsed) {
                    prevTopGender = prevParsed.topGender;
                }
            }

            return {
                profilePic: profile.profilePictureUrl || "",
                name: profile.name || profile.label || "Profile",
                platform: profile.platform,
                metricName: "Top Gender",
                metricValue: parsed ? parsed.topGender : "N/A", // Current Best or N/A
                previousMetricValue: prevTopGender, // Previous Best or N/A
                isQualitative: true // Text comparison
            };
        });
    }, [datasets, previousdatasets]);

    // 3. AI Data Preparation
    const aiChartData = useMemo(() => {
        if (!chartDataByProfile || chartDataByProfile.length === 0) return null;

        // Flatten for AI: Labels + Data per profile
        const allLabels = new Set();
        chartDataByProfile.forEach(p => p.chart.labels.forEach(l => allLabels.add(l)));
        const labels = Array.from(allLabels).sort();

        const datasets = chartDataByProfile.map(p => ({
            label: `${p.profileName} (${p.platform})`,
            data: labels.map(l => {
                const idx = p.chart.labels.indexOf(l);
                return idx >= 0 ? p.chart.datasets.data[idx] : 0;
            })
        }));

        return { labels, datasets };
    }, [chartDataByProfile]);

    const aiContext = useMemo(() => {
        return `Chart Description: Audience Gender breakdown (Pie Chart).\n\nDetails:` +
            (chartDataByProfile || []).map(p =>
                `\nProfile: ${p.profileName}\nTop Gender: ${p.topGender}\nDistribution: ${p.chart.labels.map((l, i) => `${l}=${p.chart.datasets.data[i]}`).join(', ')}`
            ).join('');
    }, [chartDataByProfile]);

    // 4. Platform Icon Helper
    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case "facebook": return <FacebookRoundedIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "youtube": return <YouTubeIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "instagram": return <InstagramIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            case "twitter": return <TwitterIcon sx={{ fontSize: 18, color: "#282828", mr: 0.5 }} />;
            default: return null;
        }
    };

    // 5. Tooltip
    const tooltipContent = (
        <div style={{ maxWidth: "350px", textAlign: "left" }}>
            <Typography variant="body2" gutterBottom>
                <strong>Description:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                This chart displays the gender distribution of the audience for each profile.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>How to read the chart:</strong>
            </Typography>
            <Typography variant="caption" display="block" gutterBottom>
                - <strong>Slices</strong>: Represent different gender groups (e.g., Male, Female, Gender Undetermined). <br />
                - <strong>Size</strong>: Proportion of the total audience.
            </Typography>

            <Typography variant="body2" gutterBottom>
                <strong>Insights you can gain:</strong>
            </Typography>
            <Typography variant="caption" display="block">
                - Tailor content tone and imagery to the dominant gender. <br />
                - Identify potential untapped audience segments. <br />
                - Compare audience composition across different competitors.
            </Typography>
        </div>
    );

    // 6. Loading State
    if (isLoading) {
        return (
            <Card>
                <MDBox p={3} display="flex" justifyContent="center" alignItems="center" height="250px">
                    <CircularProgress size={32} thickness={4} color="accent" />
                </MDBox>
            </Card>
        );
    }



    return (
        <Card sx={{ display: "flex", flexDirection: "column" }}>
            <ChartHeader
                title={title}
                description={tooltipContent}
                profilePictures={metrics.map(m => m.profilePic)}
                is_derived={is_derived}
            >
                <ChartAssistant
                    title={title}
                    context={aiContext}
                    chartData={aiChartData}
                    rawData={datasets}
                    entitlements={entitlements}
                    sx={{
                        position: "static",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                        ml: 2
                    }}
                />
            </ChartHeader>

            <div style={{ position: "relative" }}>
                {/* Entitlement Overlay */}
                {!isEntitled && (
                    <ChartNotEntitled
                        featureName={featureName}
                        planRequired={planRequired}
                    />
                )}

                <MetricCardsContainer metrics={metrics} />

                <MDBox mt={1} />

                <MDBox p={3}>
                    {(!chartDataByProfile || chartDataByProfile.length === 0) ? (
                        <MDBox display="flex" justifyContent="center" alignItems="center" height="15rem">
                            <MDTypography variant="subtitle1" color="text" align="center">No gender data available for the selected date range.</MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
                            {chartDataByProfile.map(({ profileName, platform, chart }, idx) => (
                                <MDBox key={idx} textAlign="center" width={{ xs: "100%", md: "45%", lg: "30%" }}>
                                    <MDTypography variant="subtitle2" color="text" mb={1} display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                        {getPlatformIcon(platform)} {profileName}
                                    </MDTypography>

                                    <PieChart
                                        chart={chart}
                                        height="15rem"
                                        icon={{ component: "", color: "" }}
                                        title=""
                                        description=""
                                    />

                                    {/* Legend */}
                                    {chart.datasets.data.reduce((a, b) => a + b, 0) > 0 && (
                                        <MDBox display="flex" flexWrap="wrap" justifyContent="center" mt={1} px={3}>
                                            {chart.labels.map((label, index) => (
                                                <MDBox key={label} display="flex" alignItems="center" my={0.5} mr={3} minWidth="10%">
                                                    <Icon sx={{ color: chart.datasets.backgroundColors[index], mr: 1, fontSize: "1rem" }}>
                                                        fiber_manual_record
                                                    </Icon>
                                                    <MDTypography variant="button" color="text">
                                                        {label}: {chart.datasets.data[index].toLocaleString()}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    )}
                                </MDBox>
                            ))}
                        </MDBox>
                    )}
                </MDBox>
            </div> {/* ✅ Close Relative Wrapper */}
        </Card>
    );
}

AudienceGenderChart.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    datasets: PropTypes.array.isRequired,
    previousdatasets: PropTypes.array,
    isLoading: PropTypes.bool,
    is_derived: PropTypes.bool,
    entitlements: PropTypes.object,
    isEntitled: PropTypes.bool,
    featureName: PropTypes.string,
    planRequired: PropTypes.string,
};

export default AudienceGenderChart;
